/** Copied from https://github.com/adobe/apollo-link-mutation-queue/blob/master/src/MutationQueueLink.ts */

/*
Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/
import { ApolloLink, Operation, FetchResult, NextLink } from "@apollo/client";
import { Observer, Observable } from "zen-observable-ts";

interface OperationQueueEntry {
  operation: Operation;
  forward: NextLink;
  observer: Observer<FetchResult>;
  subscription?: { unsubscribe: () => void };
}

const toRequestKey = (operation: Operation) => {
  return operation.operationName;
};

/**
 * An Apollo link that enqueues mutations so that they cannot fire in parallel.
 *
 * To skip the queue pass `{ context: { skipQueue: true } }` to your mutation.
 */
export default class MutationQueueLink extends ApolloLink {
  private opQueue: OperationQueueEntry[] = [];
  private inProcess: boolean = false;
  private debug: boolean = false;

  /**
   * @param {boolean} debug - set to true to enable logging
   */
  constructor({ debug = false }: { debug?: boolean } = {}) {
    super();
    this.debug = debug;
  }

  private log(message: string, ...rest: unknown[]) {
    if (this.debug) {
      console.log(message, ...rest);
    }
  }

  private processOperation(entry: OperationQueueEntry) {
    const { operation, forward, observer } = entry;
    this.inProcess = true;
    this.log("[PROCESSING] -", toRequestKey(operation));
    entry.subscription = forward(operation).subscribe({
      next: result => {
        this.inProcess = false;
        observer.next?.(result);
        this.log("[NEXT] -", toRequestKey(operation));

        // If there are more operations, process them.
        const nextEntry = this.opQueue.shift();
        if (nextEntry) {
          this.processOperation(nextEntry);
        }
      },
      error: error => {
        this.inProcess = false;
        observer.error?.(error);
        this.log("[ERROR] -", toRequestKey(operation), error);

        // If there are more operations, process them.
        const nextEntry = this.opQueue.shift();
        if (nextEntry) {
          this.processOperation(nextEntry);
        }
      },
      complete: observer.complete?.bind(observer),
    });
  }

  private cancelOperation(entry: OperationQueueEntry) {
    this.opQueue = this.opQueue.filter(e => e !== entry);
    entry.subscription?.unsubscribe();
  }

  private enqueue(entry: OperationQueueEntry) {
    this.log("[ENQUEUE] -", toRequestKey(entry.operation));
    this.opQueue.push(entry);
  }

  public request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    // Enqueue all mutations unless manually skipped.
    if (isMutation(operation) && !operation.getContext().skipQueue) {
      return new Observable(observer => {
        const operationEntry = { operation, forward, observer };
        if (!this.inProcess) {
          this.processOperation(operationEntry);
        } else {
          this.enqueue(operationEntry);
        }
        return () => this.cancelOperation(operationEntry);
      });
    } else {
      return forward(operation);
    }
  }
}

function isMutation(operation: Operation) {
  return operation.query.definitions.some(
    definition =>
      definition.kind === "OperationDefinition" &&
      definition.operation === "mutation"
  );
}
